import {
  CardCatalogSchema,
  DeckDefinitionSchema,
  createEngineGameplayCatalogV4,
  validateDeck,
  type CardCatalog,
  type DeckDefinition,
  type EventEffectPrograms,
} from "@fate-kingdom/card-data";

import {
  CORE_BATTLE_MATCH_CARD_CATALOG,
  CORE_BATTLE_MATCH_DECK_CARD_IDS,
  CORE_BATTLE_MATCH_TEST_DECKS,
} from "./core-battle-match-card-fixtures.js";

export const PUBLIC_BETA_TEST_CARD_DATA_VERSION = "0.8.0-public-beta-test.1";

export const PUBLIC_BETA_TEST_EVENT_IDS = {
  draw: "card.public-beta-test-draw-event",
  resources: "card.public-beta-test-resource-event",
} as const;

const PUBLIC_BETA_EFFECT_IDS = {
  draw: "effect.public-beta-test-draw-two",
  resources: "effect.public-beta-test-gain-production",
} as const;

const PUBLIC_BETA_EVENT_PROGRAMS = {
  [PUBLIC_BETA_EFFECT_IDS.draw]: { type: "draw-cards", count: 2 },
  [PUBLIC_BETA_EFFECT_IDS.resources]: {
    type: "gain-kingdom-resources",
    resources: { food: 0, production: 1, trade: 0, science: 0, culture: 0 },
  },
} as unknown as EventEffectPrograms;

function makeEvent(id: string, name: string, effectId: string, rulesText: string) {
  return {
    schemaVersion: 1,
    definitionVersion: 1,
    id,
    type: "event",
    name: `TEST ONLY ${name}`,
    cost: {},
    rulesText: `TEST ONLY: ${rulesText}`,
    resolution: {
      effects: [{ effectId }],
      destination: "discard",
    },
  };
}

export const PUBLIC_BETA_TEST_CARD_CATALOG: CardCatalog = CardCatalogSchema.parse({
  ...CORE_BATTLE_MATCH_CARD_CATALOG,
  cardDataVersion: PUBLIC_BETA_TEST_CARD_DATA_VERSION,
  cards: [
    ...CORE_BATTLE_MATCH_CARD_CATALOG.cards,
    makeEvent(
      PUBLIC_BETA_TEST_EVENT_IDS.draw,
      "Council Dispatch",
      PUBLIC_BETA_EFFECT_IDS.draw,
      "Draw two cards, respecting the public-beta hand limit.",
    ),
    makeEvent(
      PUBLIC_BETA_TEST_EVENT_IDS.resources,
      "Mobilize Workshops",
      PUBLIC_BETA_EFFECT_IDS.resources,
      "Gain one Production in the current Kingdom Pool.",
    ),
  ],
  effects: [
    ...CORE_BATTLE_MATCH_CARD_CATALOG.effects,
    {
      schemaVersion: 1,
      definitionVersion: 1,
      id: PUBLIC_BETA_EFFECT_IDS.draw,
      rulesText: "TEST ONLY: draw two cards.",
    },
    {
      schemaVersion: 1,
      definitionVersion: 1,
      id: PUBLIC_BETA_EFFECT_IDS.resources,
      rulesText: "TEST ONLY: gain one Production.",
    },
  ],
});

export const PUBLIC_BETA_TEST_ENGINE_CATALOG = createEngineGameplayCatalogV4(
  PUBLIC_BETA_TEST_CARD_CATALOG,
  PUBLIC_BETA_EVENT_PROGRAMS,
);

function makePublicBetaDeck(
  source: readonly string[],
  sourceDeck: DeckDefinition,
  eventId: string,
): DeckDefinition {
  const deck = DeckDefinitionSchema.parse({
    ...sourceDeck,
    cardDataVersion: PUBLIC_BETA_TEST_CARD_DATA_VERSION,
    id: `${sourceDeck.id}-public-beta`,
    name: `${sourceDeck.name} Public Beta`,
    cards: [...source.slice(0, 37), eventId, eventId, eventId].reduce<
      { readonly cardId: string; readonly count: number }[]
    >((entries, cardId) => {
      const existingIndex = entries.findIndex((entry) => entry.cardId === cardId);
      if (existingIndex < 0) return [...entries, { cardId, count: 1 }];
      return entries.map((entry, index) =>
        index === existingIndex ? { ...entry, count: entry.count + 1 } : entry,
      );
    }, []),
  });
  const validated = validateDeck(deck, PUBLIC_BETA_TEST_CARD_CATALOG);
  if (!validated.success) {
    throw new Error(`Invalid public-beta test deck: ${validated.error.message}`);
  }
  return validated.data;
}

export const PUBLIC_BETA_TEST_DECKS = {
  assault: makePublicBetaDeck(
    CORE_BATTLE_MATCH_DECK_CARD_IDS.assault,
    CORE_BATTLE_MATCH_TEST_DECKS.assault,
    PUBLIC_BETA_TEST_EVENT_IDS.draw,
  ),
  balanced: makePublicBetaDeck(
    CORE_BATTLE_MATCH_DECK_CARD_IDS.balanced,
    CORE_BATTLE_MATCH_TEST_DECKS.balanced,
    PUBLIC_BETA_TEST_EVENT_IDS.resources,
  ),
} as const;

function expandDeck(deck: DeckDefinition): readonly string[] {
  return deck.cards.flatMap((entry) => Array.from({ length: entry.count }, () => entry.cardId));
}

export const PUBLIC_BETA_TEST_DECK_CARD_IDS = {
  assault: expandDeck(PUBLIC_BETA_TEST_DECKS.assault),
  balanced: expandDeck(PUBLIC_BETA_TEST_DECKS.balanced),
} as const;

export function createPublicBetaMatchSetup(
  seed = "public-beta-action-window-001",
  matchId = "public-beta-action-window-match-1",
) {
  return {
    matchId,
    seed,
    rulesVersion: "public-beta-action-window-v1" as const,
    cardDataVersion: PUBLIC_BETA_TEST_CARD_DATA_VERSION,
    gameplayCatalogHash: PUBLIC_BETA_TEST_ENGINE_CATALOG.gameplayCatalogHash,
    catalog: PUBLIC_BETA_TEST_ENGINE_CATALOG,
    decks: {
      "player-1": {
        deckId: PUBLIC_BETA_TEST_DECKS.assault.id,
        cardDefinitionIds: PUBLIC_BETA_TEST_DECK_CARD_IDS.assault,
      },
      "player-2": {
        deckId: PUBLIC_BETA_TEST_DECKS.balanced.id,
        cardDefinitionIds: PUBLIC_BETA_TEST_DECK_CARD_IDS.balanced,
      },
    },
  };
}
